import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, HelpCircle } from 'lucide-react';

export default function FathomSupport() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-lg font-mono text-zinc-400 hover:text-white transition-colors">
            ← Back to Portfolio
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Support</h1>
            <p className="text-zinc-400 text-lg">Fathom - Scuba Dive Logbook</p>
          </div>

          {/* Contact Card */}
          <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-lg p-8 mb-10">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-zinc-800 rounded-lg">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Contact Us</h2>
                <p className="text-zinc-400 mb-4">
                  Have a question, feedback, or need help with Fathom? We're here to help.
                </p>
                <a
                  href="mailto:parkerdevwork@gmail.com?subject=Fathom Support"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  parkerdevwork@gmail.com
                </a>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="space-y-8">
            <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
              <HelpCircle className="w-6 h-6" />
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">Do I need an account or a signal?</h3>
                <p className="text-zinc-400">
                  No, and no. Fathom has no accounts and works entirely offline — boats, islands,
                  liveaboards, airplane mode. Your dives, certifications, species sightings,
                  photos, and signatures are all stored on your device.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">How do instructor sign-offs work?</h3>
                <p className="text-zinc-400">
                  Open a dive, tap Instructor sign-off, and hand your phone to the divemaster or
                  instructor. They sign the entry with a finger, and the signature is stored on
                  the dive with their name, agency, and the date. Signed dives show a signed
                  badge in your logbook and include the signature in the PDF export, so a dive
                  shop can verify your log the same way they would a paper book.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">What's free and what's Premium?</h3>
                <p className="text-zinc-400">
                  Free includes your first 25 dives with the full experience: sign-offs, the
                  species life list, stats, certifications, share cards, and JSON export. Fathom
                  Premium is a single one-time purchase that adds unlimited dives, PDF logbook
                  export, six photos per dive, and the light Surface theme. No subscription.
                  Whatever you choose, everything you've already logged stays fully viewable,
                  editable, and exportable, forever.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">I bought Premium on my old phone. How do I get it back?</h3>
                <p className="text-zinc-400">
                  Open Settings → Fathom Premium and tap Restore purchase while signed into the
                  same Apple ID you bought it with. Premium is a lifetime purchase tied to your
                  Apple ID, so it follows you to new devices at no extra cost.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">Can I back up or move my logbook?</h3>
                <p className="text-zinc-400">
                  Yes. Settings → Your logbook lets you export everything (dives, sites, species
                  sightings, buddies, signatures, and certifications) as a single JSON file, and
                  import it again on any device. Because your data lives only on your device,
                  we recommend exporting a backup now and then — deleting the app deletes its
                  data. Photos stay on the phone they were added on.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">My dive site isn't in the list. What do I do?</h3>
                <p className="text-zinc-400">
                  Fathom ships with about 120 famous sites for autocomplete, but custom sites are
                  first-class: type the site's name in the log form, tap "Add as a new site,"
                  pick the country and site type, and it joins your logbook like any other site,
                  including in Explore and your stats.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">Can I switch between meters and feet?</h3>
                <p className="text-zinc-400">
                  Anytime, in Settings. Dives are stored in standard units and converted for
                  display, so switching between metric (meters, bar, °C) and imperial (feet,
                  psi, °F) is instant and nothing is ever lost or rounded away.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">Is my data safe?</h3>
                <p className="text-zinc-400">
                  Yes. We don't collect any personal information and there are no accounts. Your
                  logbook stays on your device. For more details, please read our{' '}
                  <Link
                    to="/fathom/privacy-policy"
                    className="text-cyan-400 hover:text-cyan-300 underline"
                  >
                    Privacy Policy
                  </Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-800/50">
        <div className="max-w-4xl mx-auto text-center text-sm text-zinc-600">
          <p>© 2026 Fathom. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
